import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActions,
    CardMedia,
    Button,
    Chip,
    Grid,
    CircularProgress,
    Divider,
    Alert,
    Container,
    Paper
} from '@mui/material';
import axios from 'axios';
import { internshipDomain } from '../util/domain';

const GroupedEvents = () => {
    const { groupId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [group, setGroup] = useState(null);
    const [events, setEvents] = useState([]);

    useEffect(() => {
        const fetchGroupedEvents = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${internshipDomain}/api/event-groups/${groupId}`);
                if (response.data.success) {
                    setGroup(response.data.group);
                    setEvents(response.data.events);
                } else {
                    setError('Failed to load events');
                }
            } catch (err) {
                console.error('Error fetching grouped events:', err);
                setError('An error occurred while loading the event group');
            } finally {
                setLoading(false);
            }
        };

        if (groupId) {
            fetchGroupedEvents();
        }
    }, [groupId]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'OPEN':
                return 'success';
            case 'UPCOMING':
                return 'primary';
            case 'CLOSED':
                return 'default';
            default:
                return 'default';
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ my: 4 }}>
                <Alert severity="error">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!group) {
        return (
            <Box sx={{ my: 4 }}>
                <Alert severity="warning">
                    Event group not found
                </Alert>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Typography variant="h2" component="h1" gutterBottom>
                    {group.name}
                </Typography>

                {group.description && (
                    <Typography variant="body1" paragraph>
                        {group.description}
                    </Typography>
                )}

                <Divider sx={{ my: 3 }} />

                <Typography variant="h4" component="h2" gutterBottom>
                    Available Events
                </Typography>

                {events.length === 0 ? (
                    <Alert severity="warning">
                        No events are currently available in this group.
                    </Alert>
                ) : (
                    <Grid container spacing={3}>
                        {events.map((event) => (
                            <Grid item key={event.hash_id} xs={12} md={6} lg={4}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent>
                                        <Typography variant="h5" component="h3" gutterBottom>
                                            {event.title}
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                            <Chip
                                                size="small"
                                                color={getStatusColor(event.registration_status)}
                                                label={event.registration_status === 'OPEN' ? 'Registration Open' :
                                                    event.registration_status === 'UPCOMING' ? 'Coming Soon' : 'Registration Closed'}
                                            />

                                            <Chip
                                                size="small"
                                                label={event.event_type}
                                            />

                                            {event.is_online && (
                                                <Chip
                                                    size="small"
                                                    color="primary"
                                                    label="Online"
                                                />
                                            )}
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {event.description ? (
                                                event.description.length > 120 ?
                                                    `${event.description.substring(0, 120)}...` :
                                                    event.description
                                            ) : 'No description available'}
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Registration Date:</strong> {new Date(event.registration_open_date).toLocaleDateString()} {new Date(event.registration_open_date).toLocaleTimeString()}
                                                {event.registration_close_date &&
                                                    ` - ${new Date(event.registration_close_date).toLocaleDateString()} ${new Date(event.registration_close_date).toLocaleTimeString()}`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Event Date:</strong> {new Date(event.event_start_date).toLocaleDateString()} {new Date(event.event_start_date).toLocaleTimeString()}
                                                {event.event_start_date !== event.event_end_date &&
                                                    ` - ${new Date(event.event_end_date).toLocaleDateString()} ${new Date(event.event_end_date).toLocaleTimeString()}`}
                                            </Typography>


                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Registration Fee:</strong> ₹{event.registration_fee}
                                            </Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Venue:</strong> {event.venue || (event.is_online ? 'Online' : 'TBA')}
                                            </Typography>


                                        </Box>
                                    </CardContent>

                                    <Box sx={{ mt: 'auto' }}>
                                        <Divider />
                                        <CardActions>
                                            <Button
                                                component={Link}
                                                to={`/register/${event.hash_id}`}
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                disabled={event.registration_status !== 'OPEN' || event.available_slots <= 0}
                                            >
                                                {event.registration_status === 'OPEN'
                                                    ? (event.available_slots > 0 ? 'Register Now' : 'Sold Out')
                                                    : (event.registration_status === 'UPCOMING' ? 'Coming Soon' : 'Registration Closed')}
                                            </Button>
                                        </CardActions>
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Container>
    );
};

export default GroupedEvents; 